/* Conversation thread: timeline with optional channel filter + action buttons + SMS/WhatsApp inline compose */

import { Avatar, AvatarFallback, AvatarImage } from "@/v5/components/ui/avatar";
import { Button, buttonVariants } from "@/v5/components/ui/button";
import { EmojiPickerButton } from "@/v5/components/EmojiPickerButton";
import { AuthorFilterPill } from "@/v5/components/AuthorFilterPill";
import { ChannelFilterPills, type FilterChannel } from "@/v5/components/ChannelFilterPills";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/v5/components/ui/dialog";
import { ScrollArea } from "@/v5/components/ui/scroll-area";
import { Textarea } from "@/v5/components/ui/textarea";
import { SmsTemplatePicker } from "@/v5/components/SmsTemplatePicker";
import { WhatsAppApprovedMessage } from "@/v5/components/WhatsAppApprovedMessage";
import { SendFromPicker, useSendFromChoice } from "@/v5/components/SendFromPicker";
import { RecipientChips, RecipientPicker } from "@/v5/components/RecipientPicker";
import { ReplyThreadPopover } from "@/v5/components/ReplyThreadPopover";
import { buildEmailDocument } from "@/lib/emailHtml";
import { normalizeBody } from "@/v5/lib/commlogMessage";
import { scrollElementIntoCenter, scrollToBottomOf } from "@/v5/lib/scrollWithin";
import { SurveyResponseModal } from "@/pages/patients/SurveyResponseModal";
import { formatUsPhone, isPlausiblePhone } from "@/lib/phone";
import { readSourceLabel, type ReadSource } from "@/lib/readSource";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useIsBelow } from "@/hooks/useBreakpoint";
import { cn } from "@/v5/lib/utils";
import { api } from "@/v5/lib/api";
import { fetchFileViewUrl } from "@/services/patient-files.service";
import { toast } from "@/store/toastStore";
import {
  AtSign,
  CheckCheck,
  FileText,
  HeartPulse,
  Loader2,
  Mail,
  Megaphone,
  MessageCircle,
  MessageSquare,
  MessageSquareReply,
  Paperclip,
  Pause,
  Pencil,
  Phone,
  Play,
  Reply,
  Send,
  Smile,
  StickyNote,
  Trash2,
  Video,
  X,
  Zap,
} from "lucide-react";
import { Component, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactNode, ErrorInfo } from "react";

class MessageErrorBoundary extends Component<
  { messageId: string; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Message render failed:", this.props.messageId, error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-lg border border-dashed border-muted-foreground/30 px-4 py-2 text-xs text-muted-foreground">
          Message could not be displayed
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * A comm log attachment. Either an external URL (`href`) or an internal
 * patient file that must be fetched through the authenticated apiClient
 * (PatientFilesController::download) using `patientId` + `name` + `folder`.
 */
export interface AttachmentLink {
  label: string;
  href?: string;
  patientId?: number;
  name?: string;
  folder?: string;
  source?: string;
}

export interface Message {
  id: string;
  content: string;
  htmlContent?: string;
  timestamp: string;
  sender: "patient" | "provider";
  senderName: string;
  /** Staff user who authored the message (when sender === "provider"). */
  authorUserId?: number;
  senderAvatar?: string;
  type: "text" | "voice" | "video" | "image" | "link" | "call-missed" | "call-recording" | "video-recording" | "divider";
  mediaUrl?: string;
  mediaDuration?: string;
  read?: boolean;
  channel: "sms" | "email" | "whatsapp" | "phone" | "video" | "note" | "healthnexus" | "marketing";
  /**
   * Which kind of marketing traffic this is, when channel is "marketing". They
   * share one channel (one filter pill, one colour) but need distinct icons:
   * a campaign e-mail, a campaign SMS, and a patient's reply to one are three
   * different things at a glance.
   */
  marketingKind?: "email" | "sms" | "reply";
  /**
   * Delivery state label, set only when it is not a clean success — "Sending",
   * "Failed", "Bounced" and so on. Absent for a delivered message.
   */
  marketingStatus?: string;
  /**
   * Set only when an outbound message did not reach the patient. `label` is the
   * badge; `advice` is the next step, which differs per channel. Absent for
   * every message that arrived, so the ordinary case stays quiet.
   */
  deliveryFailure?: { label: string; advice: string };
  callSummary?: string;
  transcript?: string;
  replyID?: string;
  subject?: string;
  attachments?: string[];
  attachmentLinks?: Array<AttachmentLink>;
  recipients?: Array<{ userId: number; name: string }>;
  /**
   * Who marked this message as read, oldest first. Absent or empty means
   * nothing was recorded — reads made before the read log existed, and reads
   * made outside this app, leave no trace — so it must never be rendered as
   * "nobody has read this".
   *
   * `source` is the screen the read came from. Absent when none was recorded,
   * and the line then shows no screen.
   */
  readHistory?: Array<{ userId: number; name: string; readAt: string; source?: ReadSource }>;
  depth?: number;
  canEdit?: boolean;
  canDelete?: boolean;
  canReply?: boolean;
  /** True for outbound automated messages (provider-sent with userID = 0). */
  isSystem?: boolean;
  /** True when the body was truncated by the server; full content must be lazy-loaded. */
  hasFullBody?: boolean;
  /** comm_log ID for lazy-loading full body from the show endpoint. */
  commLogId?: string;
  /** surveysResponseID for "Patient Form Submitted" notes; opens the form viewer. */
  surveyResponseId?: number;
  /**
   * The lead a marketing request-info entry created; the bubble links to it.
   * Absent when the lead has since been deleted — there is nothing to open.
   */
  leadId?: number;
  /**
   * Which of the practice's own numbers this text used, ten digits.
   *
   * Always the number THIS message actually used. Undefined when nothing was
   * recorded — which is every text predating the change that started recording
   * it — and nothing is shown in that case, because staff read this number out
   * to guests who are opting back in and a guessed one sends them to a line
   * they never opted out of.
   */
  practiceNumber?: string;
}

export type ChannelFilter = "call" | "sms" | "whatsapp" | "email" | "video" | "note" | "healthnexus";

type ComposeMode = "sms" | "whatsapp";

/**
 * Open a comm log attachment. External URLs open directly; internal patient
 * files are fetched through the authenticated apiClient (so the Bearer token
 * and correct API base are applied) and opened as an object URL.
 */
async function openAttachment(attachment: AttachmentLink): Promise<void> {
  if (attachment.href) {
    window.open(attachment.href, "_blank", "noopener,noreferrer");
    return;
  }

  if (attachment.patientId == null || !attachment.name) {
    toast.error("Attachment is unavailable.");
    return;
  }

  try {
    const objectUrl = await fetchFileViewUrl(
      attachment.patientId,
      attachment.name,
      attachment.folder,
      attachment.source,
    );
    if (!objectUrl) {
      toast.error("Attachment not found.");
      return;
    }
    window.open(objectUrl, "_blank", "noopener,noreferrer");
  } catch {
    toast.error("Failed to open attachment.");
  }
}

/**
 * Which of the practice's own numbers a text used.
 *
 * The line front-desk staff scroll back for: a guest who texted STOP has to be
 * told which line to text START to, and until now nothing in the thread said
 * which one that was.
 *
 * Two wordings, one per direction:
 *
 *   received  "Received on (555) 123-4567"
 *   sent      "Sent from (555) 123-4567"
 *
 * Both are claims about THIS message, which is the only thing we will say. A
 * message with no recorded number renders nothing at all rather than naming the
 * line the practice happens to use today — that is not an answer to "which line
 * did this message use", and getting it wrong sends a guest to a line they
 * never opted out of.
 *
 * onDarkBg is not optional in practice: outbound bubbles are bg-blue-600
 * text-white, where text-muted-foreground is unreadable.
 */
function PracticeNumberLine({
  practiceNumber,
  isPatient,
  onDarkBg,
}: {
  practiceNumber: NonNullable<Message["practiceNumber"]>;
  isPatient: boolean;
  onDarkBg?: boolean;
}) {
  const formatted = formatUsPhone(practiceNumber);
  if (!formatted) return null;

  const label = isPatient ? `Received on ${formatted}` : `Sent from ${formatted}`;

  return (
    <div
      className={cn(
        "mt-2 flex items-start gap-1 text-xs",
        onDarkBg ? "text-white/85" : "text-muted-foreground",
      )}
    >
      <Phone className="mt-0.5 h-3 w-3 shrink-0" />
      <span className="min-w-0">{label}</span>
    </div>
  );
}

const TAG_VISIBLE_LIMIT = 3;

function RecipientsTagLine({
  recipients,
  onDarkBg,
}: {
  recipients: NonNullable<Message["recipients"]>;
  onDarkBg?: boolean;
}) {
  const visible = recipients.slice(0, TAG_VISIBLE_LIMIT);
  const overflow = recipients.length - visible.length;
  const overflowTitle = overflow > 0
    ? recipients.slice(TAG_VISIBLE_LIMIT).map((r) => r.name).join(", ")
    : undefined;

  return (
    <div
      className={cn(
        "mt-2 flex flex-wrap items-center gap-1 text-xs",
        onDarkBg ? "text-white/85" : "text-muted-foreground",
      )}
    >
      <AtSign className="h-3 w-3 shrink-0" />
      <span className="font-medium">Tagged:</span>
      <span className="truncate">
        {visible.map((r) => r.name).join(", ")}
        {overflow > 0 && (
          <span title={overflowTitle} className="ml-1 cursor-help underline decoration-dotted">
            +{overflow} more
          </span>
        )}
      </span>
    </div>
  );
}

/** How many read entries show before the rest are folded behind a toggle. */
const READ_HISTORY_VISIBLE_LIMIT = 3;

/**
 * "Aug 21, 2026 at 10:32 AM" — the wording the read log is specified in.
 *
 * Hand-rolled with toLocale* like every other timestamp in this file, and for
 * the same reason: the server sends a naive local-time string with no offset,
 * so it must not be pushed through anything that would treat it as UTC.
 */
function formatReadTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${day} at ${time}`;
}

/**
 * Who marked this message as read, oldest first.
 *
 * Renders nothing when there is nothing recorded. That is deliberate: reads
 * that happened before this log existed, and reads made outside this app, were
 * never captured, so an empty list is "we don't know", not "nobody read it" —
 * and a line saying so would be a claim the data does not support.
 *
 * The overflow is an inline toggle rather than a hover title: hiding part of an
 * audit trail behind a tooltip defeats the point of keeping one.
 *
 * The screen label is its own no-wrap span with the space outside it, so a
 * narrow bubble can break before "(Communication Center)" but never inside it.
 */
function ReadHistoryLine({
  readHistory,
  onDarkBg,
}: {
  readHistory: NonNullable<Message["readHistory"]>;
  onDarkBg?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const overflow = readHistory.length - READ_HISTORY_VISIBLE_LIMIT;
  const visible = expanded ? readHistory : readHistory.slice(0, READ_HISTORY_VISIBLE_LIMIT);

  return (
    <div
      className={cn(
        "mt-2 flex flex-col gap-0.5 text-xs",
        onDarkBg ? "text-white/85" : "text-muted-foreground",
      )}
    >
      {visible.map((event) => {
        const screenLabel = readSourceLabel(event.source);
        return (
          <div key={`${event.userId}-${event.readAt}`} className="flex items-start gap-1">
            <CheckCheck className="mt-0.5 h-3 w-3 shrink-0" />
            <span className="min-w-0">
              {formatReadTimestamp(event.readAt)} — Marked as read by {event.name}
              {screenLabel && (
                <>
                  {" "}
                  <span className="whitespace-nowrap">({screenLabel})</span>
                </>
              )}
            </span>
          </div>
        );
      })}
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className={cn(
            "self-start underline decoration-dotted",
            onDarkBg ? "hover:text-white" : "hover:text-foreground",
          )}
        >
          {expanded ? "Show fewer" : `Show all ${readHistory.length}`}
        </button>
      )}
    </div>
  );
}

const ACTION_BUTTONS: { value: ChannelFilter; label: string; icon: typeof Phone }[] = [
  { value: "call", label: "Call", icon: Phone },
  { value: "sms", label: "SMS", icon: MessageSquare },
  { value: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { value: "email", label: "Email", icon: Mail },
  { value: "video", label: "Video", icon: Video },
  { value: "note", label: "Note", icon: StickyNote },
  // Only rendered when the patient has a reachable Next Health portal chat
  // (see showHealthNexus) — most accounts have no portal at all.
  { value: "healthnexus", label: "HealthNexus", icon: HeartPulse },
];

const CHANNEL_COLORS: Record<
  string,
  { btnIdle: string; btnActive: string; patientBubble: string; providerBubble: string; sendBtn: string }
> = {
  // ── External communication (bold solid backgrounds) ──
  sms: {
    btnIdle: "text-primary hover:bg-primary/10 hover:text-primary",
    btnActive: "bg-primary text-white",
    patientBubble: "bg-blue-100 text-foreground",
    providerBubble: "bg-blue-600 text-white",
    sendBtn: "bg-primary hover:bg-primary/90",
  },
  whatsapp: {
    btnIdle: "text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700",
    btnActive: "bg-emerald-600 text-white",
    patientBubble: "bg-emerald-100 text-foreground",
    providerBubble: "bg-emerald-600 text-white",
    sendBtn: "bg-emerald-600 hover:bg-emerald-700",
  },
  email: {
    btnIdle: "text-orange-600 hover:bg-orange-50 hover:text-orange-700",
    btnActive: "bg-orange-600 text-white",
    patientBubble: "bg-orange-100 text-foreground",
    providerBubble: "bg-orange-600 text-white",
    sendBtn: "bg-orange-600 hover:bg-orange-700",
  },
  // ── Internal communication / logs (light bg, bold stroke) ──
  call: {
    btnIdle: "text-green-600 hover:bg-green-50 hover:text-green-700",
    btnActive: "bg-green-600 text-white",
    patientBubble: "bg-green-50 border-2 border-green-300 text-foreground",
    providerBubble: "bg-green-50 border-2 border-green-300 text-foreground",
    sendBtn: "bg-green-600 hover:bg-green-700",
  },
  video: {
    btnIdle: "text-violet-600 hover:bg-violet-50 hover:text-violet-700",
    btnActive: "bg-violet-600 text-white",
    patientBubble: "bg-violet-50 border-2 border-violet-300 text-foreground",
    providerBubble: "bg-violet-50 border-2 border-violet-300 text-foreground",
    sendBtn: "bg-violet-600 hover:bg-violet-700",
  },
  note: {
    btnIdle: "text-yellow-600 hover:bg-yellow-50 hover:text-yellow-700",
    btnActive: "bg-yellow-500 text-white",
    patientBubble: "bg-yellow-50 border-2 border-yellow-300 text-foreground",
    providerBubble: "bg-yellow-50 border-2 border-yellow-300 text-foreground",
    sendBtn: "bg-yellow-500 hover:bg-yellow-600",
  },
  phone: {
    btnIdle: "text-green-600 hover:bg-green-50 hover:text-green-700",
    btnActive: "bg-green-600 text-white",
    patientBubble: "bg-green-50 border-2 border-green-300 text-foreground",
    providerBubble: "bg-green-50 border-2 border-green-300 text-foreground",
    sendBtn: "bg-green-600 hover:bg-green-700",
  },
  healthnexus: {
    btnIdle: "text-cyan-600 hover:bg-cyan-50 hover:text-cyan-700",
    btnActive: "bg-cyan-600 text-white",
    patientBubble: "bg-cyan-100 text-foreground",
    providerBubble: "bg-cyan-600 text-white",
    sendBtn: "bg-cyan-600 hover:bg-cyan-700",
  },
  // Marketing campaign / automation sends. Read-only and always outbound, so
  // it follows the internal-log treatment (light fill + 2px stroke, identical
  // both directions) rather than the bold outbound fill used by channels staff
  // can actually reply on. Teal keeps it distinct from email (orange) and
  // SMS (blue), which it would otherwise be confused with.
  marketing: {
    btnIdle: "text-teal-600 hover:bg-teal-50 hover:text-teal-700",
    btnActive: "bg-teal-600 text-white",
    patientBubble: "bg-teal-50 border-2 border-teal-300 text-foreground",
    providerBubble: "bg-teal-50 border-2 border-teal-300 text-foreground",
    sendBtn: "bg-teal-600 hover:bg-teal-700",
  },
};

const DEFAULT_CHANNEL_COLOR = CHANNEL_COLORS.sms;

const TEXT_MESSAGE_CHANNEL_META: Partial<Record<Message["channel"], { icon: typeof Mail }>> = {
  sms: { icon: MessageSquare },
  whatsapp: { icon: MessageCircle },
  email: { icon: Mail },
  note: { icon: StickyNote },
  healthnexus: { icon: HeartPulse },
  // Fallback only — MARKETING_KIND_ICONS below overrides this per marketingKind.
  marketing: { icon: Megaphone },
};

/**
 * Icon per marketing kind, shown NEXT TO a Megaphone rather than instead of the
 * channel icon: the megaphone says "marketing", this says which medium —
 * envelope for a campaign e-mail, bubble for a campaign SMS, reply-bubble for
 * the patient's response to one.
 */
const MARKETING_KIND_ICONS: Record<NonNullable<Message["marketingKind"]>, typeof Mail> = {
  email: Mail,
  sms: MessageSquare,
  reply: MessageSquareReply,
};

/**
 * Which filter pills a message answers to.
 *
 * Everything answers to exactly one, except marketing, which answers to two:
 * its own pill AND the medium it was sent on. A campaign e-mail is still an
 * e-mail — someone filtering to Email to see everything written to this patient
 * expects it there, and its absence reads as missing history rather than as a
 * filter working correctly. The Marketing pill remains the way to see marketing
 * on its own, or to exclude it.
 */
function filterChannelsFor(message: Message): FilterChannel[] {
  const own = MSG_CHANNEL_TO_FILTER[message.channel] ?? (message.channel as FilterChannel);
  if (message.channel !== "marketing") {
    return [own];
  }
  // A request-info request from a bare link arrived on no medium at all, so
  // it answers to the Marketing pill alone.
  if (!message.marketingKind) {
    return [own];
  }
  // A reply to a campaign arrived as an SMS, so it belongs under SMS too.
  return [own, message.marketingKind === "email" ? "email" : "sms"];
}

const MSG_CHANNEL_TO_FILTER: Record<string, FilterChannel> = {
  sms: "sms",
  email: "email",
  phone: "call",
  whatsapp: "whatsapp",
  video: "video",
  note: "note",
  healthnexus: "healthnexus",
  marketing: "marketing",
};

/**
 * Recognise the synthetic id a derived marketing e-mail carries, and split it
 * back into the pieces needed to address the send.
 *
 * Marketing entries are not comm_log rows — they are derived from the campaign
 * and automation send logs, and their id is minted client-side as
 * `marketing-email-{sourceKind}-{sendID}` (see marketingEmailToCommlogEntry in
 * useCommlog). Keep the two in step.
 *
 * Returns null for a real comm_log id, which is the signal to use the ordinary
 * /communications/log/{id} route instead.
 */
function parseMarketingCommLogId(
  commLogId: string,
): { sourceKind: string; sendId: string } | null {
  const match = /^marketing-email-(campaign|automation)-(\d+)$/.exec(commLogId);
  return match ? { sourceKind: match[1], sendId: match[2] } : null;
}

function formatTimestamp(iso: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const time = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

  if (msgDay.getTime() === today.getTime()) return time;
  if (msgDay.getTime() === yesterday.getTime()) return `Yesterday ${time}`;
  if (date.getFullYear() === now.getFullYear())
    return `${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${time}`;
  return `${date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} ${time}`;
}

function formatAudioTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatDurationLabel(value?: string | number): string {
  if (value === undefined || value === null || value === "") return "";
  const seconds = typeof value === "number" ? value : Number(String(value).trim());
  if (!Number.isFinite(seconds)) return String(value);
  return formatAudioTime(seconds);
}

function getTextMessageDisplayContent(
  message: Pick<Message, "content" | "subject" | "channel" | "htmlContent">,
): string {
  const content = message.content.trim();
  if (content) {
    return content;
  }

  const subject = message.subject?.trim();
  if (subject) {
    return subject;
  }

  if (message.channel === "email") {
    return message.htmlContent ? "(Email content available)" : "(No email content)";
  }

  if (message.channel === "note") {
    return message.htmlContent ? "(Note content available)" : "(No note text)";
  }

  return "(No message text)";
}

function ClampedMessageText({
  text,
  channel,
  htmlContent,
  subject,
  marketingStatus,
  deliveryFailure,
  onOpenHtml,
  hasFullBody,
  onLoadFullBody,
  surveyResponseId,
  onOpenSurveyResponse,
  replies,
  onJumpToReply,
  onDarkBg,
  attachmentLinks,
  leadId,
}: {
  text: string;
  channel: Message["channel"];
  htmlContent?: string;
  subject?: string;
  marketingStatus?: string;
  deliveryFailure?: { label: string; advice: string };
  onOpenHtml: (rawHtml: string, subject?: string) => void;
  hasFullBody?: boolean;
  onLoadFullBody?: () => void;
  surveyResponseId?: number;
  onOpenSurveyResponse?: (responseId: number) => void;
  replies: Message[];
  onJumpToReply: (target: Message) => void;
  onDarkBg?: boolean;
  attachmentLinks?: AttachmentLink[];
  leadId?: number;
}) {
  const pRef = useRef<HTMLParagraphElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(false);
  const hasHtml = !!htmlContent;
  const hasAttachments = attachmentLinks && attachmentLinks.length > 0;

  useLayoutEffect(() => {
    if (expanded) {
      setOverflowing(false);
      return;
    }
    const el = pRef.current;
    if (!el) return;

    const measure = () => {
      setOverflowing(el.scrollHeight > el.clientHeight + 1);
    };

    measure();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, expanded]);

  const channelLabel = channel === "email" ? "email" : "message";
  const fullLabel = `View full ${channelLabel}`;

  const linkClass = cn(
    "text-xs font-medium hover:underline",
    onDarkBg ? "text-white/90 hover:text-white" : "text-primary",
  );

  const fullMessageButton = surveyResponseId && onOpenSurveyResponse ? (
    <button
      className={linkClass}
      onClick={() => onOpenSurveyResponse(surveyResponseId)}
    >
      View form responses
    </button>
  ) : hasHtml ? (
    <button
      className={linkClass}
      onClick={() =>
        onOpenHtml(htmlContent!, subject?.trim() || (channel === "email" ? "Email" : "Note"))
      }
    >
      {fullLabel}
    </button>
  ) : hasFullBody && onLoadFullBody ? (
    <button
      className={linkClass}
      onClick={onLoadFullBody}
    >
      {fullLabel}
    </button>
  ) : overflowing || expanded ? (
    <button
      className={linkClass}
      onClick={() => setExpanded((prev) => !prev)}
    >
      {expanded ? "Show less" : fullLabel}
    </button>
  ) : null;

  const repliesButton = replies.length > 0 ? (
    <ReplyThreadPopover replies={replies} onJump={onJumpToReply} onDarkBg={onDarkBg} />
  ) : null;

  // A new tab, like the thread's other link-outs, so the patient's log stays put.
  const leadLink = leadId ? (
    <a
      href={`/leads/${leadId}`}
      target="_blank"
      rel="noopener noreferrer"
      className={linkClass}
    >
      View lead
    </a>
  ) : null;

  const attachmentsToggle = hasAttachments ? (
    <button
      className={cn(linkClass, "inline-flex items-center gap-1")}
      onClick={() => setAttachmentsExpanded((prev) => !prev)}
    >
      <Paperclip className="h-3 w-3" />
      {attachmentsExpanded
        ? "Hide attachments"
        : `View attachments (${attachmentLinks.length})`}
    </button>
  ) : null;

  return (
    <>
      {/* Marketing entries show their heading like notes do: for an e-mail it
          is the campaign subject line, for an SMS the campaign name — without
          it there is nothing on the bubble saying which campaign this was. */}
      {subject?.trim() && (channel === "note" || channel === "marketing") && (
        <p className="text-sm font-bold leading-relaxed">{subject.trim()}</p>
      )}
      {/* Only set when the send is still in flight or did not reach the
          patient, so a delivered campaign message carries no badge at all. */}
      {marketingStatus && (
        <p className="text-xs font-semibold uppercase tracking-wide opacity-70">
          {marketingStatus}
        </p>
      )}
      {/* A message the patient never received. Same badge treatment as the
          campaign status above, with the next step underneath it — a bare
          "Not delivered" tells staff something is wrong and nothing about what
          to do, which is the failure this whole change exists to fix. */}
      {deliveryFailure && (
        <>
          <p className="text-xs font-semibold uppercase tracking-wide opacity-70">
            {deliveryFailure.label}
          </p>
          <p className="text-xs leading-snug opacity-70">{deliveryFailure.advice}</p>
        </>
      )}
      <p
        ref={pRef}
        className={cn(
          // `overflow-wrap-anywhere` used to sit here and resolves to nothing — it is
          // neither a Tailwind v3 utility nor a local one, so `break-words` was doing
          // all the work. Scoped below md on purpose: `anywhere` also shrinks
          // min-content width, which could narrow a desktop bubble.
          "text-sm leading-relaxed whitespace-pre-wrap break-words max-md:[overflow-wrap:anywhere]",
          !expanded && "line-clamp-5",
        )}
      >
        {text}
      </p>
      {(fullMessageButton || repliesButton || attachmentsToggle || leadLink) && (
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          {fullMessageButton}
          {leadLink}
          {attachmentsToggle}
          {repliesButton}
        </div>
      )}
      {attachmentsExpanded && hasAttachments && (
        <div className="mt-1.5 space-y-1">
          {attachmentLinks.map((attachment, idx) => (
            <button
              key={`att-${attachment.href ?? attachment.name ?? idx}`}
              type="button"
              onClick={() => { void openAttachment(attachment); }}
              className={cn(
                "flex items-center gap-1.5 text-xs font-medium hover:underline",
                onDarkBg ? "text-white/90 hover:text-white" : "text-primary",
              )}
            >
              <Paperclip className="h-3.5 w-3.5" />
              {attachment.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function AudioPlayer({ src, durationLabel }: { src: string; durationLabel?: string }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [checkedSrc, setCheckedSrc] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setPending(false);
    setError(false);
    setErrorMessage(null);
    setCheckedSrc(null);

    fetch(src, { credentials: "same-origin", signal: controller.signal })
      .then(async (res) => {
        if (controller.signal.aborted) return;
        const ct = res.headers.get("content-type") || "";
        if (ct.includes("application/json")) {
          const json = await res.json();
          if (json.status === "pending") {
            setPending(true);
            return;
          }
          if (json.type === "external_link") {
            setCheckedSrc(json.url);
            return;
          }
          if (typeof json.error === "string" && json.error.trim()) {
            setErrorMessage(json.error.trim());
          }
          setError(true);
          return;
        }
        controller.abort();
        setCheckedSrc(src);
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      });

    return () => { controller.abort(); };
  }, [src]);

  const toggle = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
    } else {
      el.play().catch(() => setError(true));
    }
  }, [playing]);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) setDuration(audioRef.current.duration);
  }, []);

  const handleEnded = useCallback(() => {
    setPlaying(false);
    setCurrentTime(0);
  }, []);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    el.currentTime = pct * duration;
    setCurrentTime(el.currentTime);
  }, [duration]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (pending) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Recording is being processed...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Phone className="h-4 w-4" />
        <span>{errorMessage ?? "Recording unavailable"}</span>
        {durationLabel && <span className="text-muted-foreground/60">{durationLabel}</span>}
      </div>
    );
  }

  if (!checkedSrc) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading recording...</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 min-w-0">
      <audio
        ref={audioRef}
        src={checkedSrc}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={handleEnded}
        onError={() => setError(true)}
      />
      <button
        onClick={toggle}
        className={cn(
          "h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors",
          playing
            ? "bg-primary text-white"
            : "bg-primary/10 text-primary hover:bg-primary/20",
        )}
      >
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div
          className="h-1.5 bg-primary/20 rounded-full overflow-hidden cursor-pointer"
          onClick={handleSeek}
        >
          <div
            className="h-full bg-primary rounded-full transition-[width] duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground tabular-nums">
          <span>{formatAudioTime(currentTime)}</span>
          <span>{duration > 0 ? formatAudioTime(duration) : (durationLabel || "--:--")}</span>
        </div>
      </div>
    </div>
  );
}

function VideoPlayer({ src }: { src: string }) {
  const [error, setError] = useState(false);

  if (error) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
        <Video className="h-4 w-4" />
        <span>Video recording unavailable.</span>
        <a
          href={src}
          target="_blank"
          rel="noreferrer"
          className="text-primary underline underline-offset-2"
        >
          Open directly
        </a>
      </div>
    );
  }

  return (
    <video
      src={src}
      controls
      preload="metadata"
      onError={() => setError(true)}
      className="w-full max-w-md rounded-lg"
      style={{ maxHeight: "320px" }}
    />
  );
}

interface ConversationThreadProps {
  patientName: string;
  patientPhone?: string;
  patientAvatar?: string;
  patientId?: string;
  planId?: string;
  smsOptIn?: boolean;
  messages: Message[];
  /**
   * `fromNumber` is the "Send from" choice, passed only for SMS while the
   * picker is shown. Resolving false means the send failed: the composer then
   * keeps the message so it can be sent again.
   */
  onSendMessage: (
    content: string,
    channel: ChannelFilter,
    replyID?: string,
    recipients?: string[],
    fromNumber?: string,
  ) => Promise<boolean | void> | boolean | void;
  onOpenCall: () => void;
  onOpenEmail: () => void;
  onOpenVideo: () => void;
  onOpenNote: () => void;
  /** Opens the HealthNexus composer. Only reachable when showHealthNexus is true. */
  onOpenHealthNexus?: () => void;
  /** Whether to offer the HealthNexus action (patient's portal chat is reachable). */
  showHealthNexus?: boolean;
  /**
   * Whether to offer WhatsApp — composing, filtering and replying. False when
   * the opt-in `whatsapp` module is not explicitly active for the account.
   *
   * This hides the AFFORDANCES only. Existing WhatsApp messages always render:
   * history is never hidden from staff who can see the rest of the thread.
   */
  showWhatsApp?: boolean;
  /**
   * Whether a plain WhatsApp message would reach this patient right now.
   *
   * THREE states, and the difference is the point. `false` means we know the
   * patient has not messaged the practice recently, so the composer warns.
   * `true` means they have. `null`/`undefined` mean we could not tell — the
   * practice has no WhatsApp, or the check failed — and must stay silent
   * rather than warn about a door that may well be open.
   */
  whatsAppWindowOpen?: boolean | null;
  /**
   * Send one of the practice's approved WhatsApp messages. Absent when the
   * host has not wired it, in which case the closed-window state falls back to
   * explaining the situation without offering to act on it.
   */
  onSendWhatsAppTemplate?: (
    contentSid: string,
    variables: Record<string, string>,
  ) => Promise<void>;
  onReply?: (message: Message) => void;
  onEdit?: (message: Message) => void;
  onDelete?: (message: Message) => void;
  /** Called when the patient's name is clicked in the thread — used to expand the detail panel. */
  onPatientNameClick?: () => void;
  replyTo?: Message | null;
  onCancelReply?: () => void;
  activeModal?: "call" | "email" | "video" | "note" | "healthnexus" | null;
  /** Called when the user scrolls near the top of the thread to load older messages. */
  onLoadOlder?: () => void;
  /** Whether more pages of older messages are available. */
  hasOlder?: boolean;
  /** Whether a load-older request is currently in flight (renders a small spinner at the top). */
  loadingOlder?: boolean;
  /** If set, scroll to and highlight this message ID once it appears in the DOM. */
  highlightMessageId?: string;
}

export function ConversationThread({
  patientId,
  patientPhone,
  planId,
  smsOptIn,
  messages,
  onSendMessage,
  onOpenCall,
  onOpenEmail,
  onOpenVideo,
  onOpenNote,
  onOpenHealthNexus,
  showHealthNexus,
  showWhatsApp,
  whatsAppWindowOpen,
  onSendWhatsAppTemplate,
  onReply,
  onEdit,
  onDelete,
  onPatientNameClick,
  replyTo,
  onCancelReply,
  activeModal,
  onLoadOlder,
  hasOlder,
  loadingOlder,
  highlightMessageId,
}: ConversationThreadProps) {
  const [messageContent, setMessageContent] = useState("");
  const [composeMode, setComposeMode] = useState<ComposeMode | null>("sms");
  const [smsTemplatePickerOpen, setSmsTemplatePickerOpen] = useState(false);
  const [inlineRecipients, setInlineRecipients] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Insert at the textarea's caret (replacing any selection), then put the
  // caret after the inserted text and re-fit the auto-growing height. Shared
  // by the SMS template picker and the emoji button.
  const insertAtCursor = useCallback((text: string) => {
    const el = textareaRef.current;
    if (!el) {
      setMessageContent((prev) => prev + text);
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    setMessageContent(el.value.slice(0, start) + text + el.value.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const cursor = start + text.length;
      el.selectionStart = el.selectionEnd = cursor;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    });
  }, []);

  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [selectedTranscript, setSelectedTranscript] = useState<string>("");
  const [channelFilter, setChannelFilter] = useState<Set<FilterChannel>>(new Set());
  const [authorFilter, setAuthorFilter] = useState<Set<number>>(new Set());
  // Persisted preference: when false, outbound system-generated messages
  // (userID = 0 on comm_log) are hidden. Patient replies to those system
  // messages keep showing because they aren't isSystem themselves.
  const [showSystemMessages, setShowSystemMessages] = useLocalStorage<boolean>(
    "commCenter.showSystemMessages",
    true,
  );

  // Reply nesting is indented in JS, so it needs a JS branch rather than a class
  // escape. Six levels at the desktop step is 144px of hard margin — more than a
  // third of a 375px screen — so the phone tier uses a step that still reads as
  // nesting without starving the bubble it indents.
  const indentStep = useIsBelow("md") ? 8 : 24;

  const filteredMessages = useMemo(() => {
    const noChannel = channelFilter.size === 0;
    const noAuthor = authorFilter.size === 0;
    if (noChannel && noAuthor && showSystemMessages) return messages;
    const channelOk = (m: Message) =>
      noChannel
      || m.type === "divider"
      || filterChannelsFor(m).some((c) => channelFilter.has(c));
    const authorOk = (m: Message) => {
      if (noAuthor || m.type === "divider") return true;
      if (m.authorUserId && authorFilter.has(m.authorUserId)) return true;
      return m.recipients?.some((r) => authorFilter.has(r.userId)) ?? false;
    };
    const systemOk = (m: Message) =>
      showSystemMessages || m.type === "divider" || !m.isSystem;
    return messages.filter((m) => channelOk(m) && authorOk(m) && systemOk(m));
  }, [messages, channelFilter, authorFilter, showSystemMessages]);

  const descendantsById = useMemo(() => {
    const repliesByParent = new Map<string, Message[]>();
    for (const m of messages) {
      if (!m.replyID || m.type === "divider") continue;
      const list = repliesByParent.get(m.replyID) ?? [];
      list.push(m);
      repliesByParent.set(m.replyID, list);
    }

    const out = new Map<string, Message[]>();
    const walk = (id: string): Message[] => {
      const cached = out.get(id);
      if (cached) return cached;
      const direct = repliesByParent.get(id) ?? [];
      const collected: Message[] = [];
      for (const child of direct) {
        collected.push(child);
        collected.push(...walk(child.id));
      }
      collected.sort((a, b) => {
        const ta = new Date(a.timestamp).getTime();
        const tb = new Date(b.timestamp).getTime();
        return (Number.isNaN(ta) ? 0 : ta) - (Number.isNaN(tb) ? 0 : tb);
      });
      out.set(id, collected);
      return collected;
    };
    for (const m of messages) walk(m.id);
    return out;
  }, [messages]);

  const [htmlModalOpen, setHtmlModalOpen] = useState(false);
  const [htmlModalContent, setHtmlModalContent] = useState("");
  const [htmlModalTitle, setHtmlModalTitle] = useState("Email");
  // A marketing body is fetched only when the dialog opens, so the dialog has
  // to be able to show itself before it has anything to render.
  const [htmlModalLoading, setHtmlModalLoading] = useState(false);
  const [htmlModalError, setHtmlModalError] = useState<string | null>(null);
  // Guards against a slow first response overwriting a second message the user
  // opened in the meantime.
  const htmlModalRequestRef = useRef(0);
  const [surveyResponseId, setSurveyResponseId] = useState<number | null>(null);

  const bottomRef = useRef<HTMLDivElement>(null);
  const isInitialScroll = useRef(true);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const pendingJumpRef = useRef<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const prependAnchorRef = useRef(false);
  const prevScrollHeightRef = useRef(0);
  const lastMessageIdRef = useRef<string | null>(null);
  // Whether the view was at (or within a bubble of) the bottom before the
  // current render. Kept by a scroll listener rather than measured in the
  // layout effect below: by the time that effect runs, a slice merged in above
  // has already grown scrollHeight, which would read as "scrolled away".
  const wasNearBottomRef = useRef(true);
  const highlightDoneRef = useRef(false);
  // Lifecycle of an inbox-navigation highlight while the log loads piecewise:
  // "pending"   — target not on screen yet; hold position and go looking for it.
  // "anchored"  — target found; keep it centered as late slices merge in above.
  // "exhausted" — every loadable page searched, no target; fall back to the
  //               newest-message default, but still center the target if a
  //               late slice (streams, marketing) finally delivers it.
  // "released"  — the user took over (scrolled, clicked); never move them again.
  const highlightAnchorRef = useRef<"pending" | "anchored" | "exhausted" | "released">(
    highlightMessageId ? "pending" : "released",
  );
  const highlightSearchPagesRef = useRef(0);

  // A layout effect so a new target's reset lands before the scroll effect
  // below reads the anchor state for it.
  useLayoutEffect(() => {
    highlightDoneRef.current = false;
    highlightSearchPagesRef.current = 0;
    highlightAnchorRef.current = highlightMessageId ? "pending" : "released";
  }, [highlightMessageId]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const release = () => {
      highlightAnchorRef.current = "released";
    };
    // Deliberate input only — a programmatic scroll fires "scroll" but none of
    // these, so the anchor survives its own centering.
    const events = ["wheel", "touchstart", "mousedown", "keydown"] as const;
    events.forEach((name) => container.addEventListener(name, release, { passive: true }));
    return () => events.forEach((name) => container.removeEventListener(name, release));
  }, []);

  useEffect(() => {
    if (!highlightMessageId || highlightDoneRef.current) return;
    if (highlightAnchorRef.current === "released") return;
    if (messages.length === 0) return;
    const el = messageRefs.current.get(highlightMessageId);
    if (!el) return;
    highlightDoneRef.current = true;
    highlightAnchorRef.current = "anchored";
    requestAnimationFrame(() => {
      const container = scrollContainerRef.current;
      if (container) scrollElementIntoCenter(container, el);
      else el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-primary", "ring-offset-2", "rounded-lg");
      window.setTimeout(() => {
        el.classList.remove("ring-2", "ring-primary", "ring-offset-2", "rounded-lg");
      }, 2000);
    });
  }, [highlightMessageId, messages]);

  const scrollAndHighlight = useCallback((id: string) => {
    const el = messageRefs.current.get(id);
    if (!el) return false;
    const container = scrollContainerRef.current;
    if (container) scrollElementIntoCenter(container, el);
    else el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-primary", "ring-offset-2", "rounded-lg");
    window.setTimeout(() => {
      el.classList.remove("ring-2", "ring-primary", "ring-offset-2", "rounded-lg");
    }, 1600);
    return true;
  }, []);

  const handleJumpToMessage = useCallback((target: Message) => {
    const filterKey = (MSG_CHANNEL_TO_FILTER[target.channel] ?? (target.channel as FilterChannel));
    if (channelFilter.size > 0 && !channelFilter.has(filterKey)) {
      const next = new Set(channelFilter);
      next.add(filterKey);
      pendingJumpRef.current = target.id;
      setChannelFilter(next);
      return;
    }
    requestAnimationFrame(() => {
      scrollAndHighlight(target.id);
    });
  }, [channelFilter, scrollAndHighlight]);

  useLayoutEffect(() => {
    const id = pendingJumpRef.current;
    if (!id) return;
    if (scrollAndHighlight(id)) {
      pendingJumpRef.current = null;
    }
  }, [filteredMessages, scrollAndHighlight]);

  const triggerLoadOlder = useCallback(() => {
    if (!onLoadOlder || !hasOlder || loadingOlder) return;
    prependAnchorRef.current = true;
    onLoadOlder();
  }, [onLoadOlder, hasOlder, loadingOlder]);

  // While the highlight target is still missing, go and get it: it may sit on
  // an older comm_log page than the first one loaded. Walks one page per
  // settle, capped at 10 pages (500 entries) so a target that no longer exists
  // cannot drag in a patient's whole history. A target in one of the
  // unpaginated slices needs no walking — it arrives on its own and the
  // centering effect above picks it up.
  useEffect(() => {
    if (!highlightMessageId || highlightAnchorRef.current !== "pending") return;
    if (messages.length === 0 || loadingOlder) return;
    if (messageRefs.current.has(highlightMessageId)) return;
    if (onLoadOlder && hasOlder && highlightSearchPagesRef.current < 10) {
      highlightSearchPagesRef.current += 1;
      triggerLoadOlder();
      return;
    }
    // Nothing left to search (or the walk hit its cap): fall back to the
    // thread's default of landing on the newest message. "exhausted" rather
    // than "released", so a target a late slice still delivers is centered
    // after all.
    highlightAnchorRef.current = "exhausted";
    const container = scrollContainerRef.current;
    if (container) scrollToBottomOf(container);
  }, [highlightMessageId, messages, hasOlder, loadingOlder, onLoadOlder, triggerLoadOlder]);

  useEffect(() => {
    if (!onLoadOlder || !hasOlder) return;
    const sentinel = topSentinelRef.current;
    const root = scrollContainerRef.current;
    if (!sentinel || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          triggerLoadOlder();
        }
      },
      { root, rootMargin: "200px 0px 0px 0px", threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadOlder, hasOlder, triggerLoadOlder]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      prevScrollHeightRef.current = container.scrollHeight;
    }
  });

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const measure = () => {
      wasNearBottomRef.current =
        container.scrollHeight - container.scrollTop - container.clientHeight < 80;
    };
    measure();
    container.addEventListener("scroll", measure, { passive: true });
    return () => container.removeEventListener("scroll", measure);
  }, []);

  useLayoutEffect(() => {
    if (messages.length === 0) {
      lastMessageIdRef.current = null;
      return;
    }

    const lastId = messages[messages.length - 1].id;
    const lastChanged = lastMessageIdRef.current !== lastId;
    lastMessageIdRef.current = lastId;

    if (prependAnchorRef.current) {
      prependAnchorRef.current = false;
      const container = scrollContainerRef.current;
      if (container) {
        const addedHeight = container.scrollHeight - prevScrollHeightRef.current;
        container.scrollTop += addedHeight;
      }
      return;
    }

    // An inbox navigation is anchored on its target message while the log's
    // pieces load (see the highlight effects above). While the anchor holds it
    // owns the viewport: content merged in above the target would otherwise
    // push it out of view, and the bottom-scroll below would abandon it.
    if (highlightMessageId && highlightAnchorRef.current === "anchored") {
      isInitialScroll.current = false;
      const container = scrollContainerRef.current;
      const el = messageRefs.current.get(highlightMessageId);
      // Instant, not smooth: a smooth re-center on every merge reads as the
      // view sliding around on its own.
      if (container && el) scrollElementIntoCenter(container, el, false);
      return;
    }
    if (highlightMessageId && highlightAnchorRef.current === "pending") {
      // Target not on screen yet — hold position rather than guessing.
      isInitialScroll.current = false;
      return;
    }

    // The timeline arrives in pieces — the comm_log page paints first, then
    // streams, marketing and HealthNexus merge in on their own clocks (see
    // useCommlog's refetch). A late slice usually inserts ABOVE the newest
    // message, so the last id alone misses it: the list grows upward and the
    // view drifts off the bottom. Stay pinned whenever the user was already
    // there; only a reader who has scrolled up is left alone.
    if (!lastChanged && !wasNearBottomRef.current) return;

    // Scroll our own container rather than bottomRef.scrollIntoView(): scrollIntoView
    // walks every scrollable ancestor on both axes — and an overflow:hidden box is
    // still programmatically scrollable — which is what dragged the whole Comm Center
    // page down and sideways on a phone (SCRUM-1362). See v5/lib/scrollWithin.ts.
    const container = scrollContainerRef.current;
    if (!container) return;

    // A re-pin (content merged in above while at the bottom) jumps instantly so
    // the view reads as never having moved; only a genuinely new newest message
    // animates.
    scrollToBottomOf(container, lastChanged && !isInitialScroll.current);
    isInitialScroll.current = false;
  }, [messages.length, highlightMessageId]);

  const handleActionClick = (action: ChannelFilter) => {
    switch (action) {
      case "sms":
        setComposeMode("sms");
        textareaRef.current?.focus();
        break;
      case "whatsapp":
        // Defensive: the button is not rendered when WhatsApp is off.
        if (!showWhatsApp) break;
        setComposeMode("whatsapp");
        textareaRef.current?.focus();
        break;
      case "call":
        setComposeMode(null);
        onOpenCall();
        break;
      case "email":
        setComposeMode(null);
        onOpenEmail();
        break;
      case "video":
        setComposeMode(null);
        onOpenVideo();
        break;
      case "note":
        setComposeMode(null);
        onOpenNote();
        break;
      case "healthnexus":
        setComposeMode(null);
        onOpenHealthNexus?.();
        break;
    }
  };

  /**
   * Whether a message can be replied to inline. WhatsApp replies are gated with
   * the rest of the WhatsApp affordances, so an existing WhatsApp message still
   * renders but offers no Reply while the module is off.
   */
  const canReplyTo = (message: Message) =>
    message.channel !== "whatsapp" || showWhatsApp === true;

  const isInlineReply =
    replyTo &&
    (replyTo.channel === "sms" || (replyTo.channel === "whatsapp" && showWhatsApp === true));

  // Only a text being answered moves the "Send from" choice to its line.
  const sendFrom = useSendFromChoice(patientId, replyTo?.channel === "sms" ? replyTo : null);
  // "Choose a number…" is showing: a text sent now would have no sender.
  const needsSendFrom = composeMode === "sms" && sendFrom.needsChoice;

  useEffect(() => {
    if (replyTo?.channel === "sms") {
      setComposeMode("sms");
      textareaRef.current?.focus();
    } else if (replyTo?.channel === "whatsapp" && showWhatsApp) {
      setComposeMode("whatsapp");
      textareaRef.current?.focus();
    }
  }, [replyTo, showWhatsApp]);

  // 7–15 digits, not a US ten: an international patient can be texted too
  // (SCRUM-1833). The server builds the Twilio number from the stored code.
  const phoneIsValid = isPlausiblePhone(patientPhone);
  const trimmedPhone = (patientPhone ?? "").trim();
  const smsAllowed = smsOptIn !== false;

  // Phone validity blocks BOTH channels — the server rejects an unusable number
  // on either (INVALID_PHONE / PHONE_PATIENT_MISMATCH are not channel-scoped),
  // so there is no point making staff wait for the round trip.
  //
  // The opt-in check stays SMS-only, matching the server: patients.smsOptIn is
  // SMS/TCPA consent, and WhatsApp consent lives with Meta. Reusing it here
  // would invent a consent rule the backend does not share.
  const composeBlocked =
    composeMode !== null && (!phoneIsValid || (composeMode === "sms" && !smsAllowed));
  const composeBlockReason = composeMode === null
    ? null
    : composeMode === "sms" && !smsAllowed
      ? "SMS not authorized for patient."
      : !trimmedPhone
        ? "No cell phone number entered."
        : !phoneIsValid
          ? "Invalid phone number."
          : null;

  const handleSend = async () => {
    if (composeBlocked) return;
    if (!messageContent.trim() || !composeMode || sending || needsSendFrom) return;
    const replyID = isInlineReply ? replyTo.id : undefined;
    const fromNumber = composeMode === "sms" ? sendFrom.fromNumber : undefined;
    setSending(true);
    try {
      const sent = await onSendMessage(messageContent, composeMode, replyID, inlineRecipients, fromNumber);
      if (sent === false) return;
      setMessageContent("");
      setInlineRecipients([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOpenTranscript = (transcript: string) => {
    setSelectedTranscript(transcript);
    setTranscriptOpen(true);
  };

  const handleOpenHtmlModal = (rawHtml: string, subject?: string) => {
    const resolvedTitle = subject?.trim() || "Email";
    // Abandon any in-flight lazy load, so its response cannot land on top of
    // the body being opened here.
    htmlModalRequestRef.current++;
    setHtmlModalContent(buildEmailDocument(rawHtml));
    setHtmlModalTitle(resolvedTitle);
    setHtmlModalLoading(false);
    setHtmlModalError(null);
    setHtmlModalOpen(true);
  };

  const handleLazyLoadFullBody = useCallback(async (commLogId: string, subject?: string) => {
    // Open FIRST, fetch second. The body is a round trip away, and waiting for
    // it before showing anything makes the click read as if it did nothing.
    const requestId = ++htmlModalRequestRef.current;
    const isStale = () => requestId !== htmlModalRequestRef.current;

    setHtmlModalTitle(subject?.trim() || "Email");
    setHtmlModalContent("");
    setHtmlModalError(null);
    setHtmlModalLoading(true);
    setHtmlModalOpen(true);

    try {
      // Marketing entries are derived, not comm_log rows: their id is the
      // synthetic key minted in marketingEmailToCommlogEntry, and their body
      // comes from the per-send marketing endpoint rather than /communications/
      // log/{id}, which would 404 on it.
      const marketing = parseMarketingCommLogId(commLogId);
      const path = marketing
        ? `/communications/comm-log/patient/${patientId}/marketing/${marketing.sourceKind}/${marketing.sendId}/body`
        : `/communications/log/${commLogId}`;

      const result = await api<{ data: { notes?: string; emailBody?: string; htmlContent?: string } }>(
        path,
        { noRedirectOn401: true },
      );
      if (isStale()) return;

      const body = normalizeBody(
        result?.data?.notes || result?.data?.emailBody || result?.data?.htmlContent || "",
      );
      if (!body.trim()) {
        setHtmlModalError("This message has no content to display.");
        return;
      }
      setHtmlModalContent(buildEmailDocument(body));
    } catch {
      if (isStale()) return;
      // Reported inside the dialog rather than as a toast: the dialog is
      // already open by this point, and pulling it closed under the user would
      // read as a second, unexplained failure.
      setHtmlModalError("Failed to load the full message.");
    } finally {
      if (!isStale()) setHtmlModalLoading(false);
    }
  }, [patientId]);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
      {/*
        Action Buttons (create-new, not filters)

        Eight labelled pills need roughly 600px. They fit the ≥1024 column and are
        clipped by the parent's overflow-hidden below it, so the row scrolls on its
        own account rather than pushing the page sideways. `max-lg:` not `max-md:`
        because the 768–1023 column is only ~448px wide and clips them too.
      */}
      <div className="flex flex-shrink-0 items-center gap-1.5 border-b border-border bg-white px-4 py-2 max-lg:flex-nowrap max-lg:overflow-x-auto">
        {ACTION_BUTTONS.filter(
          (btn) =>
            (btn.value !== "healthnexus" || showHealthNexus === true) &&
            (btn.value !== "whatsapp" || showWhatsApp === true),
        ).map((btn) => {
          const Icon = btn.icon;
          const isInlineActive =
            (btn.value === "sms" && composeMode === "sms") ||
            (btn.value === "whatsapp" && composeMode === "whatsapp") ||
            (btn.value === "call" && activeModal === "call") ||
            (btn.value === "email" && activeModal === "email") ||
            (btn.value === "video" && activeModal === "video") ||
            (btn.value === "note" && activeModal === "note") ||
            (btn.value === "healthnexus" && activeModal === "healthnexus");
          const colors = CHANNEL_COLORS[btn.value] || DEFAULT_CHANNEL_COLOR;
          return (
            <button
              key={btn.value}
              onClick={() => handleActionClick(btn.value)}
              className={cn(
                "flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors max-lg:shrink-0",
                isInlineActive ? colors.btnActive : colors.btnIdle,
              )}
            >
              <Icon className="h-4 w-4" />
              {btn.label}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-1 border-b border-slate-100 bg-slate-50/60 px-3 py-1.5 max-lg:overflow-x-auto">
        <ChannelFilterPills
          activeChannels={channelFilter}
          onChange={setChannelFilter}
          className="border-b-0 bg-transparent p-0"
          // Removes the ability to filter TO WhatsApp; it never hides messages.
          // channelFilter is plain component state (not persisted), so no stale
          // "whatsapp" selection can survive and start excluding rows.
          exclude={showWhatsApp ? undefined : ["whatsapp"]}
        />
        <label
          className={cn(
            "flex cursor-pointer items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors max-lg:shrink-0 max-lg:whitespace-nowrap",
            showSystemMessages
              ? "bg-slate-200 text-slate-700"
              : "text-slate-500 hover:bg-slate-100 hover:text-slate-600",
          )}
          title="Show outbound system-generated messages"
        >
          <input
            type="checkbox"
            checked={showSystemMessages}
            onChange={(e) => setShowSystemMessages(e.target.checked)}
            className="h-3 w-3 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-400"
          />
          System Messages
        </label>
        <div className="ml-auto max-lg:shrink-0">
          <AuthorFilterPill selected={authorFilter} onChange={setAuthorFilter} />
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto" style={{ overflowAnchor: "none" }}>
        {/*
          max-lg:pr-9 clears the AI assistant's edge tab, which is `fixed right-0
          top-1/2 … lg:hidden` and would otherwise sit on top of right-aligned
          bubbles. `max-lg:` matches the tab's own `lg:hidden` exactly, including
          while printing — a fixed element still paints at the ~816px print width,
          so gutter and tab appear and disappear together.
        */}
        <div className="space-y-4 px-6 py-4 max-md:px-3 max-lg:pr-9">
          <div ref={topSentinelRef} aria-hidden className="h-px w-full" />
          {(loadingOlder || hasOlder) && (
            <div className="flex items-center justify-center py-2 text-xs text-muted-foreground">
              {loadingOlder ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Loading older messages…
                </span>
              ) : (
                <span className="opacity-60">Scroll up for older messages</span>
              )}
            </div>
          )}
          {filteredMessages.map((message) => {
            const textDisplayContent = message.type === "text"
              ? getTextMessageDisplayContent(message)
              : message.content;

            const isPatient = message.sender === "patient";
            const depthOffset = message.depth ? Math.min(message.depth, 6) * indentStep : 0;
            const textChannelMeta = TEXT_MESSAGE_CHANNEL_META[message.channel];
            const TextChannelIcon = message.marketingKind
              ? MARKETING_KIND_ICONS[message.marketingKind]
              : textChannelMeta?.icon;
            const isDarkBubble =
              message.type === "text" &&
              !isPatient &&
              !message.htmlContent &&
              (message.channel === "sms" ||
                message.channel === "whatsapp" ||
                message.channel === "email");

            return (
              <MessageErrorBoundary key={message.id} messageId={message.id}>
              <div
                data-message-id={message.id}
                ref={(el) => {
                  if (el) {
                    messageRefs.current.set(message.id, el);
                  } else {
                    messageRefs.current.delete(message.id);
                  }
                }}
                className="space-y-2 scroll-mt-24 rounded-lg transition-shadow"
                style={depthOffset ? { marginLeft: depthOffset } : undefined}
              >
                <div className={cn(
                  "flex items-center gap-2",
                  isPatient ? "justify-start" : "justify-end"
                )}>
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={message.senderAvatar} alt={message.senderName} />
                    <AvatarFallback className={cn(
                      "text-xs",
                      isPatient ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                    )}>
                      {message.senderName
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  {isPatient && onPatientNameClick ? (
                    <button
                      type="button"
                      onClick={onPatientNameClick}
                      title="Open patient details"
                      className="text-xs font-medium text-foreground hover:underline cursor-pointer"
                    >
                      {message.senderName}
                    </button>
                  ) : (
                    <span className="text-xs font-medium text-foreground">
                      {message.senderName}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {formatTimestamp(message.timestamp)}
                  </span>
                </div>

                {message.type === "text" && (
                  <div className={cn(
                    "flex group/msg",
                    isPatient ? "justify-start" : "justify-end"
                  )}>
                    {!isPatient && (onReply || onEdit || onDelete) && (
                      <div className="mr-1.5 flex self-center opacity-0 transition-opacity group-hover/msg:opacity-100">
                        {onDelete && message.canDelete ? (
                          <button
                            onClick={() => onDelete(message)}
                            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                        {onEdit && message.canEdit ? (
                          <button
                            onClick={() => onEdit(message)}
                            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            title="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                        {onReply && message.canReply !== false && canReplyTo(message) ? (
                          <button
                            onClick={() => onReply(message)}
                            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            title={message.channel === "note" ? "Add linked note" : "Reply"}
                          >
                            {message.channel === "note" ? (
                              <StickyNote className="h-3.5 w-3.5" />
                            ) : (
                              <Reply className="h-3.5 w-3.5" />
                            )}
                          </button>
                        ) : null}
                      </div>
                    )}
                    <div className={cn(
                      // 70% of a phone-width column leaves too little once the
                      // padding and the hover actions are taken out, which is what
                      // wrapped thread text one or two words per line.
                      "relative max-w-[70%] rounded-lg px-4 py-2.5 overflow-hidden max-md:max-w-[88%]",
                      // Marketing carries two icons, so it needs more room.
                      message.marketingKind ? "pr-14 max-md:pr-11" : "pr-10 max-md:pr-8",
                      (() => {
                        const mc = CHANNEL_COLORS[message.channel] || DEFAULT_CHANNEL_COLOR;
                        if (message.htmlContent) {
                          const htmlColors: Record<string, string> = {
                            email: "bg-orange-50 border-2 border-orange-300 text-foreground",
                            note: "bg-yellow-50 border-2 border-yellow-300 text-foreground",
                            // Every marketing e-mail has htmlContent, so without
                            // this it would fall through to the grey default and
                            // lose the teal that marks it as marketing.
                            marketing: "bg-teal-50 border-2 border-teal-300 text-foreground",
                          };
                          return htmlColors[message.channel] || "bg-muted/50 border-2 border-border text-foreground";
                        }
                        return isPatient ? mc.patientBubble : mc.providerBubble;
                      })(),
                    )}>
                      {(TextChannelIcon || message.marketingKind) && (
                        <div className="absolute right-3 top-3 flex items-center gap-1 opacity-80">
                          {/* Marketing pairs the megaphone with the medium's own
                              icon — megaphone + envelope, megaphone + bubble —
                              so "this is marketing" and "this was an e-mail"
                              both read at a glance. */}
                          {message.marketingKind && (
                            <Megaphone className="h-3.5 w-3.5 flex-shrink-0" />
                          )}
                          {TextChannelIcon && (
                            <TextChannelIcon className="h-3.5 w-3.5 flex-shrink-0" />
                          )}
                        </div>
                      )}
                      <ClampedMessageText
                        text={textDisplayContent}
                        channel={message.channel}
                        htmlContent={message.htmlContent}
                        subject={message.subject}
                        marketingStatus={message.marketingStatus}
                        deliveryFailure={message.deliveryFailure}
                        onOpenHtml={handleOpenHtmlModal}
                        hasFullBody={message.hasFullBody}
                        onLoadFullBody={message.hasFullBody && message.commLogId
                          ? () => { void handleLazyLoadFullBody(message.commLogId!, message.subject); }
                          : undefined}
                        surveyResponseId={message.surveyResponseId}
                        onOpenSurveyResponse={patientId ? setSurveyResponseId : undefined}
                        replies={descendantsById.get(message.id) ?? []}
                        onJumpToReply={handleJumpToMessage}
                        onDarkBg={isDarkBubble}
                        attachmentLinks={message.attachmentLinks}
                        leadId={message.leadId}
                      />
                      {message.practiceNumber && (
                        <PracticeNumberLine
                          practiceNumber={message.practiceNumber}
                          isPatient={isPatient}
                          onDarkBg={isDarkBubble}
                        />
                      )}
                      {message.recipients && message.recipients.length > 0 && (
                        <RecipientsTagLine recipients={message.recipients} onDarkBg={isDarkBubble} />
                      )}
                      {message.readHistory && message.readHistory.length > 0 && (
                        <ReadHistoryLine readHistory={message.readHistory} onDarkBg={isDarkBubble} />
                      )}
                    </div>
                    {isPatient && (onReply || onEdit || onDelete) && (
                      <div className="ml-1.5 flex self-center opacity-0 transition-opacity group-hover/msg:opacity-100">
                        {onReply && message.canReply !== false && canReplyTo(message) ? (
                          <button
                            onClick={() => onReply(message)}
                            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            title={message.channel === "note" ? "Add linked note" : "Reply"}
                          >
                            {message.channel === "note" ? (
                              <StickyNote className="h-3.5 w-3.5" />
                            ) : (
                              <Reply className="h-3.5 w-3.5" />
                            )}
                          </button>
                        ) : null}
                        {onEdit && message.canEdit ? (
                          <button
                            onClick={() => onEdit(message)}
                            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            title="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                        {onDelete && message.canDelete ? (
                          <button
                            onClick={() => onDelete(message)}
                            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                      </div>
                    )}
                  </div>
                )}

                {message.type === "call-recording" && (
                  <div className={cn("space-y-2 max-w-md", isPatient ? "mr-auto" : "ml-auto")}>
                    <div className="relative rounded-lg bg-muted p-3 pr-10 space-y-2">
                      <Phone className="absolute right-3 top-3 h-4 w-4 text-primary flex-shrink-0" />
                      <div className="flex items-center gap-2 pr-6">
                        <span className="text-sm font-medium text-foreground">Recording</span>
                        {formatDurationLabel(message.mediaDuration) && (
                          <span className="text-xs text-muted-foreground">{formatDurationLabel(message.mediaDuration)}</span>
                        )}
                        <div className="flex-1" />
                        {message.transcript && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenTranscript(message.transcript!)}
                            className="h-7 px-2 text-xs hover:bg-primary/10 hover:text-primary flex-shrink-0"
                          >
                            <FileText className="h-3.5 w-3.5 mr-1" />
                            Transcript
                          </Button>
                        )}
                      </div>
                      {message.mediaUrl ? (
                        <AudioPlayer src={message.mediaUrl} durationLabel={message.mediaDuration} />
                      ) : (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                          <span>Recording not available</span>
                        </div>
                      )}
                    </div>

                    {message.callSummary && (
                      <div className="ml-7">
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {message.callSummary}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {message.type === "video-recording" && (
                  <div className={cn("space-y-2 max-w-md", isPatient ? "mr-auto" : "ml-auto")}>
                    <div className="relative rounded-lg bg-muted p-3 pr-10 space-y-2">
                      <Video className="absolute right-3 top-3 h-4 w-4 text-primary flex-shrink-0" />
                      <div className="flex items-center gap-2 pr-6">
                        <span className="text-sm font-medium text-foreground">Video Recording</span>
                        {formatDurationLabel(message.mediaDuration) && (
                          <span className="text-xs text-muted-foreground">{formatDurationLabel(message.mediaDuration)}</span>
                        )}
                        <div className="flex-1" />
                        {message.transcript && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenTranscript(message.transcript!)}
                            className="h-7 px-2 text-xs hover:bg-primary/10 hover:text-primary flex-shrink-0"
                          >
                            <FileText className="h-3.5 w-3.5 mr-1" />
                            Transcript
                          </Button>
                        )}
                      </div>
                      {message.mediaUrl ? (
                        <VideoPlayer src={message.mediaUrl} />
                      ) : (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                          <span>{message.callSummary || "Recording not available"}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {message.type === "call-missed" && (
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <Phone className="h-4 w-4" />
                    <span className="font-medium">{message.content}</span>
                    <span className="text-xs text-muted-foreground">{formatTimestamp(message.timestamp)}</span>
                  </div>
                )}
              </div>
              </MessageErrorBoundary>
            );
          })}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* SMS / WhatsApp Inline Compose */}
      {composeMode && (
        // py-3 is split rather than kept alongside the safe-area utility: both set
        // padding-bottom at the same specificity, so which one won would depend on
        // emitted source order. pb-safe-plus-3 is calc(0.75rem + env(...)), and
        // env() is 0px wherever no inset is reported — so desktop still computes
        // exactly the 0.75rem py-3 gave it. It clears the iOS home indicator, which
        // otherwise sits over the send button on a phone.
        <div className="flex-shrink-0 border-t border-border bg-white px-4 pt-3 pb-safe-plus-3 max-md:px-3">
          {composeBlocked && composeBlockReason ? (
            <div className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">To:</span>{" "}
              <span className="italic">{composeBlockReason}</span>
            </div>
          ) : (
            <>
              {/* WhatsApp will not carry a message the practice writes unless
                  the patient has messaged them there recently. Warn, never
                  block: this is inferred from the messages we know about, so it
                  can be wrong in both directions, and a member of staff who
                  wants to try anyway must always be able to. Shown only on an
                  explicit false — null and undefined mean we could not tell,
                  which must never be presented as a closed door. */}
              {composeMode === "whatsapp" && whatsAppWindowOpen === false && (
                onSendWhatsAppTemplate ? (
                  <WhatsAppApprovedMessage
                    patientId={patientId}
                    onSend={onSendWhatsAppTemplate}
                  />
                ) : (
                  /* No sender wired: still say why free text will not arrive,
                     rather than letting staff type into a dead composer. */
                  <div className="mb-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-xs font-semibold text-amber-900">
                      This message may not reach the patient.
                    </p>
                    <p className="text-xs text-amber-800 leading-snug mt-0.5">
                      WhatsApp only lets you message a patient for a day after they message
                      you, and this patient hasn't recently. Ask them to message you on
                      WhatsApp first, or send this as a text message, which will go through
                      now.
                    </p>
                  </div>
                )
              )}
              {isInlineReply && (
                <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded-lg bg-muted/60 border border-border">
                  <Reply className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                  <span className="text-xs text-muted-foreground truncate flex-1">
                    Replying to: {(() => {
                      const replyPreview = getTextMessageDisplayContent(replyTo!);
                      return replyPreview.length > 80 ? replyPreview.substring(0, 80) + "..." : replyPreview;
                    })()}
                  </span>
                  {onCancelReply && (
                    <button
                      onClick={onCancelReply}
                      className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground flex-shrink-0"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              )}
              {composeMode === "sms" && sendFrom.visible && (
                <SendFromPicker
                  options={sendFrom.options}
                  value={sendFrom.selected}
                  onChange={sendFrom.setSelected}
                  hint={sendFrom.selectedOption?.hint ?? null}
                  disabled={sending}
                />
              )}
              {inlineRecipients.length > 0 && (
                <div className="mb-2">
                  <RecipientChips
                    value={inlineRecipients}
                    onChange={setInlineRecipients}
                    planId={planId}
                  />
                </div>
              )}
              <div className="flex items-end gap-2">
                {/* Two rows so the three tools take two buttons' width, leaving
                    the textarea the room: template on top, right-aligned over
                    the emoji; tag and emoji beneath. */}
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 rounded-lg text-amber-600 hover:bg-amber-50 hover:text-amber-700 flex-shrink-0"
                    title="Insert Template"
                    onClick={() => setSmsTemplatePickerOpen(true)}
                  >
                    <Zap className="h-4.5 w-4.5" />
                  </Button>
                  <div className="flex items-center gap-1">
                    <RecipientPicker
                      value={inlineRecipients}
                      onChange={setInlineRecipients}
                      planId={planId}
                      compact
                    />
                    <EmojiPickerButton
                      onSelect={insertAtCursor}
                      disabled={sending}
                      className={buttonVariants({
                        variant: "ghost",
                        size: "icon",
                        className: "h-9 w-9 rounded-lg text-muted-foreground hover:text-foreground flex-shrink-0",
                      })}
                    >
                      <Smile className="h-4.5 w-4.5" />
                    </EmojiPickerButton>
                  </div>
                </div>

                <div className="flex-1 relative">
                  <Textarea
                    ref={textareaRef}
                    value={messageContent}
                    onChange={(e) => {
                      setMessageContent(e.target.value);
                      const el = e.target;
                      el.style.height = "auto";
                      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
                    }}
                    onKeyDown={handleKeyPress}
                    disabled={sending}
                    placeholder={`Type ${composeMode === "whatsapp" ? "WhatsApp" : "SMS"} message...`}
                    className="min-h-[44px] max-h-[120px] resize-none pr-12 rounded-2xl border-border overflow-y-auto"
                  />
                </div>

                <Button
                  onClick={handleSend}
                  disabled={!messageContent.trim() || sending || needsSendFrom}
                  size="icon"
                  className={cn(
                    "h-10 w-10 rounded-full flex-shrink-0",
                    CHANNEL_COLORS[composeMode].sendBtn,
                  )}
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Transcript Dialog */}
      <Dialog open={transcriptOpen} onOpenChange={setTranscriptOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Call Transcript</DialogTitle>
            <DialogDescription>
              Automated transcription of the call recording
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            <div className="space-y-4">
              {selectedTranscript.split('\n').map((line, index) => {
                const isProvider = line.startsWith('Provider:') || line.startsWith('Support Agent:');
                const isPatientLine = line.startsWith('Patient:') || line.includes('Richards:');

                return (
                  <div key={index} className="space-y-1">
                    {(isProvider || isPatientLine) ? (
                      <div className={cn(
                        "flex gap-3",
                        isProvider && "justify-end"
                      )}>
                        <div className={cn(
                          "max-w-[80%] rounded-lg px-4 py-2.5 overflow-hidden break-words",
                          isProvider
                            ? "bg-primary/10 text-foreground"
                            : "bg-muted text-foreground"
                        )}>
                          <p className="text-xs font-semibold mb-1 text-primary">
                            {line.split(':')[0]}
                          </p>
                          <p className="text-sm leading-relaxed">
                            {line.split(':').slice(1).join(':').trim()}
                          </p>
                        </div>
                      </div>
                    ) : line.trim() && (
                      <p className="text-sm text-muted-foreground italic text-center py-2">
                        {line}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* HTML Content Modal */}
      <Dialog open={htmlModalOpen} onOpenChange={setHtmlModalOpen}>
        <DialogContent className="max-w-6xl w-[90vw] sm:max-w-none max-h-[85vh] flex flex-col max-md:w-screen max-md:max-w-none" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{htmlModalTitle}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto min-h-0">
            {htmlModalLoading ? (
              <div
                className="flex items-center justify-center gap-2 text-sm text-muted-foreground"
                style={{ minHeight: "60vh" }}
              >
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading message…
              </div>
            ) : htmlModalError ? (
              <div
                className="flex items-center justify-center text-sm text-muted-foreground"
                style={{ minHeight: "60vh" }}
              >
                {htmlModalError}
              </div>
            ) : htmlModalOpen ? (
              <iframe
                sandbox="allow-same-origin"
                srcDoc={htmlModalContent}
                className="w-full border-none block"
                style={{ minHeight: "60vh" }}
                title={htmlModalTitle}
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {patientId && (
        <SurveyResponseModal
          patientId={Number(patientId)}
          responseId={surveyResponseId}
          open={surveyResponseId !== null}
          onClose={() => setSurveyResponseId(null)}
        />
      )}

      <SmsTemplatePicker
        open={smsTemplatePickerOpen}
        onOpenChange={setSmsTemplatePickerOpen}
        patientId={patientId}
        planId={planId}
        onApply={insertAtCursor}
      />
    </div>
  );
}
